import RetryContext from 'utils/retry/RetryContext';
import RetryPolicy from 'utils/retry/RetryPolicy';
import { scheduleTimeout } from 'utils/retry/timeout';
import { timeUtils, Duration } from '@instinet-analytics/core-lib';

const { advanceTime } = timeUtils;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RetryAction = (...args: any[]) => Promise<any>;

interface RetryFunctionOptions {
    action: RetryAction;
    retryPolicy: RetryPolicy;
    retryLog?: (durationMs: number) => void;
}

export default class RetryFunction {
    private action: RetryAction;
    private retryContext: RetryContext;
    private retryPolicy: RetryPolicy;
    private lastRetryTime: Duration;
    public readonly retryLog?: (durationMs: number) => void;
    private cancelled = false;

    public constructor(options: RetryFunctionOptions) {
        this.action = options.action;
        this.retryPolicy = options.retryPolicy;
        this.retryLog = options.retryLog;

        this.retryContext = {
            count: 0,
            cumulativeTime: 0,
            error: undefined,
        };
        this.lastRetryTime = Date.now();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async exec(...args: any[]): Promise<any> {
        try {
            if (this.cancelled) {
                return;
            }
            return await Promise.race([
                scheduleTimeout(this.retryPolicy.retryTimeout),
                this.tryExec(...args),
            ]);
        } catch (error) {
            this.setRetryContext(error as Error);

            if (!this.shouldRetry()) {
                throw error;
            }

            await this.advanceToNextRetry();

            return await this.exec(...args);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async tryExec(...args: any[]): Promise<any> {
        return await this.action(...args);
    }

    private setRetryContext(error: Error): void {
        const retryTime = Date.now();
        this.retryContext.count++;
        this.retryContext.cumulativeTime += retryTime - this.lastRetryTime;
        this.retryContext.error = error;
        this.lastRetryTime = retryTime;
    }

    private shouldRetry(): boolean {
        return this.retryPolicy.shouldRetry(this.retryContext);
    }

    private async advanceToNextRetry(): Promise<void> {
        const backOffDuration = this.retryPolicy.backOff(this.retryContext);
        if (this.retryLog) {
            this.retryLog(backOffDuration);
        }
        await advanceTime(backOffDuration);
    }

    public cancel() {
        this.cancelled = true;
    }
}
